import type { AnalyticsEventName } from './events'
import { isOnAnalyticsExcludedPath } from './excluded-paths'
import type { AnalyticsParams } from './params'
import { ANALYTICS_READY_EVENT, isAnalyticsDispatchReady, trackEvent } from './track'

/**
 * Dispatch for the marketing-site events (links, sections, demos, FAQ).
 *
 * The site header, footer, sections and FAQ also render on capability URLs
 * (`/invite/*`, `/promotion/*`, `/magic-login`), and once GA and the Pixel are
 * installed by an earlier page, anything sent there carries the tokenised
 * address as `page_location` / `dl`. The check reads `window.location` at
 * dispatch time, per the `event-tracking` skill: it belongs at the call site,
 * not in `track.ts`, which also carries conversions that are legitimate on
 * `/magic-login`.
 */
export function trackSiteEvent(name: AnalyticsEventName, params: AnalyticsParams = {}): void {
  if (isOnAnalyticsExcludedPath()) return
  trackEvent(name, params)
}

/**
 * {@link trackSiteEvent} for impressions that may fire before GA and the Pixel
 * have loaded. It defers like `trackEventWhenReady`, but checks the address
 * again when the queued event is finally sent: the visitor may have navigated
 * onto a capability URL in the meantime.
 */
export function trackSiteEventWhenReady(
  name: AnalyticsEventName,
  params: AnalyticsParams = {}
): void {
  if (typeof window === 'undefined') return
  if (isAnalyticsDispatchReady()) {
    trackSiteEvent(name, params)
    return
  }
  const flush = () => {
    if (!isAnalyticsDispatchReady()) return
    window.removeEventListener(ANALYTICS_READY_EVENT, flush)
    trackSiteEvent(name, params)
  }
  window.addEventListener(ANALYTICS_READY_EVENT, flush)
}
